import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STORY_SUBMIT_URL = 'https://readdy.ai/api/form/d6j4nrgdenotc3br4d10';

const STORY_PROMPTS = [
  'The day my cat chose me...',
  'My cat\'s funniest moment was...',
  'My cat always knows when I\'m sad because...',
  'The weirdest thing my cat does is...',
  'My cat and I have a special ritual where...',
];

const FEATURED_STORIES = [
  {
    id: 1,
    name: 'Sarah M.',
    location: 'Austin, TX',
    catName: 'Mochi',
    story: 'Mochi showed up on my doorstep during a thunderstorm, soaking wet and meowing at the top of her lungs. I opened the door just to check on her and she walked straight in, jumped on my couch, and never left. That was 4 years ago. Best uninvited guest I\'ve ever had.',
    emoji: '🌧️',
    likes: 342,
  },
  {
    id: 2,
    name: 'James T.',
    location: 'Portland, OR',
    catName: 'Sir Fluffington',
    story: 'Every morning at exactly 6:47am, Sir Fluffington sits on my chest and stares at me until I wake up. No alarm needed. He\'s never been off by more than 2 minutes in 3 years. Scientists should study him.',
    emoji: '⏰',
    likes: 518,
  },
  {
    id: 3,
    name: 'Priya K.',
    location: 'Chicago, IL',
    catName: 'Luna',
    story: 'Luna learned how to open the treat cabinet. Not just bat at it — she actually figured out the latch. We had to install a child lock. She figured that out too. We now keep treats in a locked box. She\'s working on it.',
    emoji: '🔓',
    likes: 891,
  },
];

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function CatStorySection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [catName, setCatName] = useState('');
  const [story, setStory] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [likedStories, setLikedStories] = useState<Set<number>>(new Set());
  const [storyLikes, setStoryLikes] = useState<Record<number, number>>(
    Object.fromEntries(FEATURED_STORIES.map(s => [s.id, s.likes]))
  );
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const charCount = story.length;
  const maxChars = 500;

  const handlePromptClick = (prompt: string) => {
    setSelectedPrompt(prompt);
    setStory(prompt + ' ');
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setPhotoError(null);
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setPhotoError('Please upload a JPG, PNG, GIF, or WEBP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Photo must be under 5MB.');
      return;
    }

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLike = (id: number) => {
    setLikedStories(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setStoryLikes(l => ({ ...l, [id]: l[id] - 1 }));
      } else {
        next.add(id);
        setStoryLikes(l => ({ ...l, [id]: l[id] + 1 }));
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !story || story.length > maxChars) return;

    setSubmitStatus('submitting');

    try {
      const params = new URLSearchParams();
      params.append('name', name);
      params.append('email', email);
      params.append('catName', catName);
      params.append('story', story);
      params.append('catPhoto', photoFile ? 'Uncollectable' : 'No photo');

      const res = await fetch(STORY_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setName('');
        setEmail('');
        setCatName('');
        setStory('');
        setSelectedPrompt(null);
        handleRemovePhoto();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-teal-50 to-rose-50">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-rose-100 text-rose-600 text-sm font-bold rounded-full mb-4">
            <i className="ri-quill-pen-line"></i>
            Cat Stories
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Write Your Cat's Story 🐾
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Every cat has a tale worth telling. Share yours with thousands of fellow cat lovers.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Left — Story Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-md p-6 md:p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Share Your Story</h3>
              <p className="text-sm text-gray-500 mb-5">Need inspiration? Pick a prompt below to get started.</p>

              {/* Prompts */}
              <div className="flex flex-wrap gap-2 mb-5">
                {STORY_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => handlePromptClick(prompt)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap font-medium ${
                      selectedPrompt === prompt
                        ? 'bg-teal-500 text-white border-teal-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-teal-400 hover:text-teal-600'
                    }`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                {submitStatus === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-10"
                  >
                    <div className="text-5xl mb-4">🎉</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Story Submitted!</h4>
                    <p className="text-gray-500 text-sm mb-5">
                      Thank you for sharing! We'll feature the best stories on our site.
                    </p>
                    <button
                      onClick={() => setSubmitStatus('idle')}
                      className="px-5 py-2 bg-teal-500 text-white rounded-full text-sm font-bold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Write Another Story
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          required
                          placeholder="Jane Doe"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Cat's Name</label>
                        <input
                          type="text"
                          name="catName"
                          value={catName}
                          onChange={e => setCatName(e.target.value)}
                          placeholder="Whiskers"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Your Email * <span className="text-gray-400 font-normal">(to be notified if featured)</span></label>
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        placeholder="you@example.com"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Your Story *</label>
                      <textarea
                        name="story"
                        value={story}
                        onChange={e => {
                          if (e.target.value.length <= maxChars) setStory(e.target.value);
                        }}
                        required
                        rows={5}
                        placeholder="Tell us about your cat..."
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400 transition-colors resize-none"
                      />
                      <div className={`text-right text-xs mt-1 ${charCount >= maxChars ? 'text-rose-500 font-bold' : 'text-gray-400'}`}>
                        {charCount}/{maxChars}
                      </div>
                    </div>

                    {/* Cat Photo Upload */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Cat Photo <span className="text-gray-400 font-normal">(optional · JPG, PNG, GIF, WEBP · max 5MB)</span>
                      </label>

                      {photoPreview ? (
                        <div className="relative w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                          <img
                            src={photoPreview}
                            alt="Cat preview"
                            className="w-full h-40 object-cover object-top"
                          />
                          <button
                            type="button"
                            onClick={handleRemovePhoto}
                            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow text-gray-600 hover:text-rose-500 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                          <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <i className="ri-image-line"></i>
                            {photoFile?.name}
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full border-2 border-dashed border-gray-200 hover:border-teal-400 rounded-xl py-6 flex flex-col items-center gap-2 text-gray-400 hover:text-teal-500 transition-all cursor-pointer bg-gray-50 hover:bg-teal-50/30"
                        >
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
                            <i className="ri-camera-line text-xl"></i>
                          </div>
                          <span className="text-sm font-medium">Click to upload a photo of your cat</span>
                          <span className="text-xs">JPG, PNG, GIF or WEBP</span>
                        </button>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        name="catPhoto"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />

                      {photoError && (
                        <p className="text-rose-500 text-xs mt-1 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {photoError}
                        </p>
                      )}
                    </div>

                    {submitStatus === 'error' && (
                      <p className="text-rose-500 text-xs">Something went wrong. Please try again.</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitStatus === 'submitting' || !name || !email || !story || charCount > maxChars}
                      className="w-full py-3 bg-rose-500 hover:bg-rose-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold rounded-xl transition-all text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {submitStatus === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill"></i>
                          Share My Story
                        </>
                      )}
                    </button>

                    <p className="text-center text-xs text-gray-400">
                      By submitting, you agree to let us feature your story on our site 🐱
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right — Featured Stories */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col gap-4"
          >
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-star-fill text-amber-400"></i>
              <h3 className="text-lg font-bold text-gray-900">Featured Stories</h3>
            </div>

            {FEATURED_STORIES.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-rose-100 text-xl">
                      {item.emoji}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-400">{item.location} · Cat: <span className="text-teal-600 font-semibold">{item.catName}</span></p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleLike(item.id)}
                    className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      likedStories.has(item.id)
                        ? 'bg-rose-50 border-rose-300 text-rose-500'
                        : 'bg-white border-gray-200 text-gray-400 hover:border-rose-300 hover:text-rose-400'
                    }`}
                  >
                    <i className={likedStories.has(item.id) ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                    {storyLikes[item.id]}
                  </button>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed line-clamp-4">
                  "{item.story}"
                </p>
              </motion.div>
            ))}

            <p className="text-center text-xs text-gray-400 mt-2">
              Submit your story to be featured here! ✨
            </p>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
